import React, { useState, useEffect } from 'react';
import { TextInput, Button, Box, Select, Textarea, Notification, Title, Grid, Col, Input, Text, Divider } from '@mantine/core';
import axios from 'axios';
import { IconAt } from '@tabler/icons-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import HoneypotField from '../../../common/components/HoneypotField';
import WafCaptchaModal from '../../../common/components/WafCaptchaModal';
import { ensureWafSdk, isWafChallengeStatus, wafTokenHeader } from '../../../common/utils/wafToken';

export interface ContactUsProps {
    reason?: string;
}

const ContactUs = ({ reason }: ContactUsProps) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        businessName: '',
        phone: '',
        message: '',
        reason: reason ?? '',
        flowMeters: '',
        cowIdSystem: '',
        numberOfCows: '',
        numberOfMilkingStations: '',
        location: '',
        integrator: '',
        integratorContactDetails: '',
        dairyManagementSoftware: '',
        // Bot trap - never rendered to a person. See common/components/HoneypotField.
        website: ''
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [captchaOpen, setCaptchaOpen] = useState(false);

    // Load the WAF SDK with the form, not in index.html: the landing pages must not pay for a script
    // they never use, and loading it here gives it the whole time the person spends typing to acquire
    // a token in the background.
    useEffect(() => {
        void ensureWafSdk();
    }, []);

    const handleChange = (name: string, value: string | null | undefined) => {
        setFormData((prev) => ({ ...prev, [name]: value ?? '' }));
    };

    const submitContact = async (isRetry: boolean) => {
        setLoading(true);
        setError('');
        setSuccess(false);

        // WAF answers a request it will not let through with 202/405, before linok-api ever sees it.
        // 202 comes back as a resolved axios response; 405 as a rejection - hence both branches.
        const handleChallenge = () => {
            if (isRetry) {
                setError('We could not verify your browser. Please try again.');
            } else {
                setCaptchaOpen(true);
            }
        };

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            // A missing token is not an error: the header is simply absent and WAF decides.
            const response = await axios.post(`${apiUrl}/api/contact`, formData, {
                headers: await wafTokenHeader(),
            });
            if (isWafChallengeStatus(response.status)) {
                handleChallenge();
            } else if (response.status === 200) {
                setSuccess(true);
                setFormData((prev) => ({
                    ...prev,
                    name: '',
                    email: '',
                    businessName: '',
                    phone: '',
                    message: '',
                    flowMeters: '',
                    cowIdSystem: '',
                    numberOfCows: '',
                    numberOfMilkingStations: '',
                    location: '',
                    integrator: '',
                    integratorContactDetails: '',
                    dairyManagementSoftware: '',
                }));
            }
        } catch (err) {
            if (axios.isAxiosError(err) && isWafChallengeStatus(err.response?.status)) {
                handleChallenge();
                return;
            }
            console.error(err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void submitContact(false);
    };

    // The SDK caches the token minted by a solved puzzle, so re-submitting picks it up. One retry only.
    const handleCaptchaSolved = () => {
        setCaptchaOpen(false);
        void submitContact(true);
    };

    const fieldStyle = {
        marginBottom: '1rem',
    };

    const inputStyles = {
        input: {
            backgroundColor: '#f1f3f5',
            border: '1px solid #ced4da',
            '&:focus': {
                borderColor: '#156b83',
                backgroundColor: '#fff',
            },
        },
        label: {
            fontWeight: 600,
            color: '#0F2137',
            marginBottom: '0.35rem',
        },
    };

    const subLabelStyle = {
        fontWeight: 600,
        color: '#0F2137',
    };

    return (
        <Box style={{ maxWidth: 800, margin: 'auto', padding: '1.5rem', borderRadius: '8px', backgroundColor: 'white' }}>
            <style>{`
                .linok-phone-input .PhoneInputInput {
                    background-color: #f1f3f5;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    padding: 0.5rem 0.75rem;
                    font-size: 16px;
                    height: 42px;
                    outline: none;
                    transition: border-color 0.15s ease, background-color 0.15s ease;
                }
                .linok-phone-input .PhoneInputInput:focus {
                    border-color: #156b83;
                    background-color: #fff;
                }
            `}</style>
            <WafCaptchaModal
                opened={captchaOpen}
                onClose={() => setCaptchaOpen(false)}
                onSolved={handleCaptchaSolved}
            />
            <form onSubmit={handleSubmit}>
                <HoneypotField value={formData.website} onChange={(v) => handleChange('website', v)} />
                <Grid gutter="md">
                    <Col xs={12} sm={6}>
                        <TextInput
                            size='md'
                            placeholder="Your name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                            style={fieldStyle}
                            styles={inputStyles}
                        />


                        <TextInput
                            size='md'
                            icon={<IconAt size={16} />}
                            placeholder="Your email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <PhoneInput
                            defaultCountry="US"
                            placeholder="Your phone number"
                            value={formData.phone}
                            style={{ ...fieldStyle }}
                            className="linok-phone-input"
                            onChange={(value) => handleChange('phone', value)}
                            required />
                        <Text size="sm" style={subLabelStyle} mb="xs">Do you have Cow ID system?</Text>

                        <Select
                            title="Do you have Cow ID system?"
                            size='sm'
                            placeholder="Please select"
                            data={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ]}
                            value={formData.cowIdSystem}
                            onChange={(value) => handleChange('cowIdSystem', value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <TextInput
                            size='md'
                            placeholder="Your business name"
                            value={formData.businessName}
                            onChange={(e) => handleChange('businessName', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <Textarea
                            size='md'
                            placeholder="Your message"
                            value={formData.message}
                            onChange={(e) => handleChange('message', e.target.value)}
                            minRows={4}
                            style={{ ...fieldStyle, height: 'auto' }}
                            styles={inputStyles}
                        />
                        <Text size="sm" style={subLabelStyle} mb="xs">Do you have flow meters installed?</Text>

                        <Select
                            title=""
                            size='sm'
                            placeholder="Please select"
                            data={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ]}
                            value={formData.flowMeters}
                            onChange={(value) => handleChange('flowMeters', value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                </Grid>
                <Grid>
                    <Divider />
                    <Title order={3}>Optional Information</Title>
                    <Divider />
                </Grid>

                {/* First row - 3 questions */}
                <Grid gutter="md" style={{ marginTop: '1rem' }}>
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Number of cows</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter number of cows"
                            value={formData.numberOfCows}
                            onChange={(e) => handleChange('numberOfCows', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Number of milking stations</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter number of milking stations"
                            value={formData.numberOfMilkingStations}
                            onChange={(e) => handleChange('numberOfMilkingStations', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Location</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter your location"
                            value={formData.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                </Grid>

                {/* Second row - 3 questions */}
                <Grid gutter="md">
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Integrator Name</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter integrator information"
                            value={formData.integrator}
                            onChange={(e) => handleChange('integrator', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Integrator contact details</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter integrator contact details"
                            value={formData.integratorContactDetails}
                            onChange={(e) => handleChange('integratorContactDetails', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                    <Col xs={12} sm={4}>
                        <Text size="sm" style={subLabelStyle} mb="xs">Dairy management software</Text>
                        <TextInput
                            size='sm'
                            placeholder="Enter software name"
                            value={formData.dairyManagementSoftware}
                            onChange={(e) => handleChange('dairyManagementSoftware', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />
                    </Col>
                </Grid>

                {error && (
                    <Notification color="red" onClose={() => setError('')} style={{ marginBottom: '1rem' }}>
                        {error}
                    </Notification>
                )}
                {success && (
                    <Notification color="green" onClose={() => setSuccess(false)} style={{ marginBottom: '1rem' }}>
                        Message sent successfully!
                    </Notification>
                )}

                <Button type="submit" fullWidth loading={loading} style={{ marginTop: '1rem' }}>
                    {loading ? 'Sending...' : 'Send Message'}
                </Button>
            </form>
        </Box>
    );
};

export default ContactUs;
